/**
 * Read-only view for care coordination session notes.
 * @version 4
 */
import { CARE_COORDINATION_SCHEMA } from "@/components/Provider/PatientView/MemberProfile/schema/CareCoordinationSchema";
import dayjs from "@/lib/helpers/dayjs";
import { eventTypeDisplayNameFactory } from "@/components/Provider/ScheduleAvailability/ActiveScheduleDetails";
import Label from "@/components/UI/Label";
import type { NoteDetail } from "@/core/types";
import { RATING_OPTION_LABEL_MAP } from "@/constants/rating";

import { CallFocusCheckboxes } from "../components/CallFocusCheckboxes";
import { ICD10List } from "../components/ICD10List";
import { PatientOutcomeRatings } from "../components/PatientOutcomeRatings";
import { ReadonlyDischarge } from "../components/ReadonlyDischarge";

type CareCoordinationViewProps = {
  note: NoteDetail;
};

export function CareCoordinationView({ note }: CareCoordinationViewProps) {
  const noteRecord = note as unknown as Record<string, unknown>;
  const noteContent = (note.content ?? {}) as Record<string, unknown>;

  return (
    <>
      <div className="text-left txt-sec fs-16 tw:font-bold">Care Coordination</div>
      <div className="row print-wrap">
        {CARE_COORDINATION_SCHEMA.map((formEl) => {
          let content = noteRecord[formEl.name];

          if (formEl.name.startsWith("content.")) {
            content = noteContent[formEl.name.split(".")[1]];
          }

          if (formEl.name === "content.discharges") {
            return (content as Record<string, unknown>[]).map((discharge, index) => (
              <ReadonlyDischarge discharge={discharge} key={index} />
            ));
          }

          if (formEl.name === "signature") return null;

          return (
            <div className={`${formEl.elementClasses}`} key={formEl.name}>
              {formEl.elementConfig.label && <Label>{formEl.elementConfig.label}</Label>}
              {!formEl.elementConfig.label && <br />}

              {formEl.name === "content.call_focus" && (
                <div className="row print-wrap mx-2 mb-3">
                  <CallFocusCheckboxes isDisabled options={content as Record<string, boolean>} />
                </div>
              )}

              {formEl.name === "content.patient_outcome_answer" && (
                <div className="mb-3 print-width">
                  <PatientOutcomeRatings
                    answer={content}
                    isDisabled
                    ratingOptionLabelMap={RATING_OPTION_LABEL_MAP}
                  />
                </div>
              )}

              {formEl.name === "call_type" && (
                <div className="mb-3">{eventTypeDisplayNameFactory(content)}</div>
              )}

              {formEl.name === "session_from" && (
                <div className="session-from-display mb-3">
                  {dayjs(content as string, "HH:mm").format("hh:mm a")}
                </div>
              )}

              {formEl.name.includes("ICD_10_axis") && (
                <div className="mb-3">
                  {content && (
                    <ul>
                      <ICD10List data={content as Parameters<typeof ICD10List>[0]["data"]} />
                    </ul>
                  )}
                </div>
              )}

              {typeof content === "string" &&
                formEl.name !== "call_type" &&
                formEl.name !== "session_from" &&
                !formEl.name.includes("ICD_10_axis") && <div className="mb-3">{content}</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}
